import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/hooks';
import { useUI } from '@/hooks/ui/useUI';
import { useTableSearch } from '@/hooks/ui/table/useTableSearch';
import { formatDateToYYYYMMDD } from '@/utils';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import { TextArea } from '@/components/ui/inputs';
import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';
import { attendanceService } from '@/api/domains/attendance';
import { createAttendanceTranslationHelpers } from '@/api/domains/attendance/types/managedAttendance.types';
import {
  ATTENDANCE_ACTION_MAP,
  type ManagedAttendanceRecord,
  type ManagedAttendanceFilters,
  type ManagedAttendanceStatus,
} from '@/api/domains/attendance';

// Constants
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_DATE_RANGE_DAYS = 30;
const STATUS_BACKEND_MAP: Record<string, string> = {
  pending: 'Pendiente',
  approved: 'Aprobado',
  rejected: 'Rechazado',
};

/**
 * Map UI column names to backend API orderBy parameter names
 * UI columns may have different names than API parameters
 */
const COLUMN_TO_API_ORDER_BY: Record<string, string> = {
  creationDate: 'createdDate',        // UI column -> API parameter
  workdayDate: 'workdayDate',         // passthrough
  markingTime: 'markTime',            // UI field -> API parameter
  eventType: 'eventName',             // UI field -> API parameter
  action: 'actionName',               // UI field -> API parameter
  modificationDate: 'actionDate',     // UI field -> API parameter
  status: 'status',                   // passthrough
};

// Module-level component so it can hold its own useState for the controlled TextArea.
// Reused by both reject-disabled and reject-modified handlers.
interface RejectionReasonInputProps {
  labelText: string;
  placeholder: string;
  onValueChange: (value: string) => void;
}

const RejectionReasonInput = ({
  labelText,
  placeholder,
  onValueChange,
}: RejectionReasonInputProps) => {
  const [value, setValue] = useState('');
  return (
    <TextArea
      label={labelText}
      required
      value={value}
      placeholder={placeholder}
      maxLength={500}
      rows={4}
      showCharacterCount
      onChange={(newValue) => {
        setValue(newValue);
        onValueChange(newValue);
      }}
    />
  );
};

export interface StatusFilterOption {
  value: string;
  label: string;
}

export interface UseManagedAttendanceReturn {
  filters: ManagedAttendanceFilters;
  setFilters: (
    updater:
      | ManagedAttendanceFilters
      | ((previous: ManagedAttendanceFilters) => ManagedAttendanceFilters),
  ) => void;
  defaultFrom: string;
  defaultTo: string;
  data: ManagedAttendanceRecord[];
  totalRecords: number;
  isLoading: boolean;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  onSearch: (text: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  columns: TableColumn<ManagedAttendanceRecord>[];
  actions: TableAction<ManagedAttendanceRecord>[];
  viewDetails: (row: ManagedAttendanceRecord) => void;
  statusFilterOptions: StatusFilterOption[];
  actionFilterOptions: StatusFilterOption[];
}

export const useManagedAttendance = (): UseManagedAttendanceReturn => {
  const {
    openSidePanel, showNotification, showError, showApiError, openModal, closeModal,
  } = useUI();
  const { t: translate } = useTranslation();
  const employeeId = useAppSelector((state) => state.auth.user?.idEmpleado ?? 0);

  const { debouncedSearchText, setSearchText } = useTableSearch();

  const getDefaultDateRange = (): { from: string; to: string } => {
    const currentDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(currentDate.getDate() - DEFAULT_DATE_RANGE_DAYS);
    return {
      from: formatDateToYYYYMMDD(fromDate),
      to: formatDateToYYYYMMDD(currentDate),
    };
  };

  const [filters, setFilters] = useState<ManagedAttendanceFilters>(() => ({
    ...getDefaultDateRange(),
    status: 'all',
  }));
  
  const defaultFrom = useMemo(() => {
    const currentDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(currentDate.getDate() - DEFAULT_DATE_RANGE_DAYS);
    return formatDateToYYYYMMDD(fromDate);
  }, []);
  
  const defaultTo = useMemo(() => formatDateToYYYYMMDD(new Date()), []);

  // Server-side pagination and sort state
  const [rows, setRows] = useState<ManagedAttendanceRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<{ orderBy?: string; direction?: 'ASC' | 'DESC' }>({
    orderBy: 'createdDate',
    direction: 'DESC',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mapStatusToBackend = useCallback(
    (status: ManagedAttendanceStatus | 'all' | null | undefined): string | null => {
      if (!status || status === 'all') return null;
      const backendValue = STATUS_BACKEND_MAP[status];
      return backendValue || null;
    },
    [],
  );

  const fetchList = useCallback(async (
    pageNum: number,
    pageSizeNum: number,
    sortState: { orderBy?: string; direction?: 'ASC' | 'DESC' },
  ) => {
    if (!employeeId) return;

    setIsLoading(true);
    try {
      const response = await attendanceService.getManagedAttendance(employeeId, {
        page: pageNum,
        pageSize: pageSizeNum,
        textSearch: debouncedSearchText,
        orderBy: sortState.orderBy,
        orderDirection: sortState.direction,
        dateFrom: filters.from,
        dateTo: filters.to,
        status: mapStatusToBackend(filters.status),
        actionName: filters.action && filters.action !== 'all' ? filters.action : null,
      });

      if (response.success && response.data) {
        setRows(response.data.data);
        setTotalRecords(response.data.totalRecords);
      } else {
        setRows([]);
        setTotalRecords(0);
        if (response.error) {
          showApiError(response);
        }
      }
    } catch {
      showError(translate('common:errors.unexpected'));
      setRows([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    employeeId,
    debouncedSearchText,
    filters,
    mapStatusToBackend,
    showApiError,
    showError,
    translate,
  ]);

  // Fetch data when pagination, sorting, filters, or search changes
  useEffect(() => {
    void fetchList(page, pageSize, sort);
  }, [fetchList, page, pageSize, sort]);

  // Helper to refetch data with current state
  const refetchData = useCallback(() => {
    void fetchList(page, pageSize, sort);
  }, [fetchList, page, pageSize, sort]);

  const onSearch = useCallback((text: string) => {
    setSearchText(text);
    setPage(1); // Reset to first page when searching
  }, [setSearchText]);

  const onSortChange = useCallback(
    (columnKey: string, direction: 'asc' | 'desc') => {
      // Map UI column name to API parameter name
      const apiOrderBy = COLUMN_TO_API_ORDER_BY[columnKey] || columnKey;
      setSort({
        orderBy: apiOrderBy,
        direction: direction.toUpperCase() as 'ASC' | 'DESC',
      });
      setPage(1); // Reset to first page when sorting changes
    },
    [],
  );

  const setPageSizeSafe = useCallback((size: number) => {
    const normalized = Number(size) || DEFAULT_PAGE_SIZE;
    setPageSize(normalized);
    setPage(1); // Reset to first page when page size changes
  }, []);

  const viewDetails = useCallback((row: ManagedAttendanceRecord) => {
    const title = row.action === 'incident' 
      ? translate('attendance:managedAttendance.detail.incidentTitle')
      : translate('attendance:managedAttendance.detail.title');
    
    openSidePanel({
      component: 'ManagedAttendanceDetail',
      title,
      props: { record: row },
    });
  }, [openSidePanel, translate]);

  const handleApprove = useCallback((record: ManagedAttendanceRecord) => {
    openModal('ConfirmModal', {
      variant: 'success',
      icon: 'check',
      title: translate('attendance:managedAttendance.modals.approve.title'),
      bodyText: translate('attendance:managedAttendance.modals.approve.body'),
      confirmText: translate('attendance:managedAttendance.modals.approve.confirm'),
      onConfirm: async () => {
        if (!record.markId) {
          showError(translate('common:errors.unexpected'));
          closeModal('ConfirmModal');
          return;
        }
        setIsSubmitting(true);
        const response = await attendanceService.updateManagedMarkStatus(
          employeeId,
          record.markId,
          { status: 'approved', comment: '' },
        );
        setIsSubmitting(false);
        if (response.success) {
          showNotification('success', translate('attendance:managedAttendance.messages.approved'), 4000);
          closeModal('ConfirmModal');
          refetchData();
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          showError(response.error.validationErrors[0].errorMessage ?? translate('common:errors.unexpected'));
          closeModal('ConfirmModal');
        } else {
          showApiError(response);
          closeModal('ConfirmModal');
        }
      },
      onCancel: () => closeModal('ConfirmModal'),
    });
  }, [
    openModal, closeModal, showNotification, showApiError,
    showError, translate, refetchData, employeeId,
  ]);

  const handleReject = useCallback((record: ManagedAttendanceRecord) => {
    const rejectionReasonValue = { current: '' };
    openModal('ConfirmModal', {
      variant: 'error',
      icon: 'alert',
      title: translate('attendance:managedAttendance.modals.reject.title'),
      bodyText: translate('attendance:managedAttendance.modals.reject.body'),
      confirmText: translate('attendance:managedAttendance.modals.reject.confirm'),
      children: (
        <div className="flex flex-col gap-1">
          <label
            htmlFor="managed-attendance-reject-reason"
            className="text-sm font-medium"
            style={{ color: colors.primary.main }}
          >
            {translate('attendance:managedAttendance.modals.reject.reasonLabel')}
            <span className="ml-1" style={{ color: colors.feedback.error300 }}>*</span>
          </label>
          <textarea
            id="managed-attendance-reject-reason"
            onChange={(event) => { rejectionReasonValue.current = event.target.value; }}
            placeholder={translate('attendance:managedAttendance.modals.reject.reasonPlaceholder')}
            maxLength={500}
            rows={4}
            className="w-full rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2"
            style={{ borderColor: colors.grays.neutralCC, color: colors.grays.neutral33 }}
          />
        </div>
      ),
      onConfirm: async () => {
        const rejectionReason = rejectionReasonValue.current.trim();
        if (!rejectionReason) {
          showError(translate('attendance:managedAttendance.modals.reject.reasonRequired'));
          return;
        }
        if (!record.markId) {
          showError(translate('common:errors.unexpected'));
          closeModal('ConfirmModal');
          return;
        }
        setIsSubmitting(true);
        const response = await attendanceService.updateManagedMarkStatus(
          employeeId,
          record.markId,
          { status: 'rejected', comment: rejectionReason },
        );
        setIsSubmitting(false);
        if (response.success) {
          showNotification('success', translate('attendance:managedAttendance.messages.rejected'), 4000);
          closeModal('ConfirmModal');
          refetchData();
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          showError(response.error.validationErrors[0].errorMessage ?? translate('common:errors.unexpected'));
          closeModal('ConfirmModal');
        } else {
          showApiError(response);
          closeModal('ConfirmModal');
        }
      },
      onCancel: () => closeModal('ConfirmModal'),
    });
  }, [
    openModal, closeModal, showNotification, showApiError,
    showError, translate, refetchData, employeeId,
  ]);

  const handleApproveDisabled = useCallback((record: ManagedAttendanceRecord) => {
    openModal('ConfirmModal', {
      variant: 'success',
      icon: 'check',
      title: translate('attendance:managedAttendance.modals.approveDisabled.title'),
      bodyText: translate('attendance:managedAttendance.modals.approveDisabled.body'),
      confirmText: translate('attendance:managedAttendance.modals.approveDisabled.confirm'),
      onConfirm: async () => {
        if (!record.markId) {
          showError(translate('common:errors.unexpected'));
          closeModal('ConfirmModal');
          return;
        }
        setIsSubmitting(true);
        const response = await attendanceService.updateManagedMarkStatus(
          employeeId,
          record.markId,
          { status: 'approved', comment: '' },
        );
        setIsSubmitting(false);
        if (response.success) {
          showNotification('success', translate('attendance:managedAttendance.messages.disabledApproved'), 4000);
          closeModal('ConfirmModal');
          refetchData();
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          showError(response.error.validationErrors[0].errorMessage ?? translate('common:errors.unexpected'));
          closeModal('ConfirmModal');
        } else {
          showApiError(response);
          closeModal('ConfirmModal');
        }
      },
      onCancel: () => closeModal('ConfirmModal'),
    });
  }, [
    openModal, closeModal, showNotification, showApiError,
    showError, translate, refetchData, employeeId,
  ]);

  const handleRejectDisabled = useCallback((record: ManagedAttendanceRecord) => {
    const rejectionReasonValue = { current: '' };
    openModal('ConfirmModal', {
      variant: 'error',
      icon: 'alert',
      title: translate('attendance:managedAttendance.modals.rejectDisabled.title'),
      bodyText: translate('attendance:managedAttendance.modals.rejectDisabled.body'),
      confirmText: translate('attendance:managedAttendance.modals.rejectDisabled.confirm'),
      children: (
        <RejectionReasonInput
          labelText={translate('attendance:managedAttendance.modals.rejectDisabled.reasonLabel')}
          placeholder={translate('attendance:managedAttendance.modals.rejectDisabled.reasonPlaceholder')}
          onValueChange={(value) => { rejectionReasonValue.current = value; }}
        />
      ),
      onConfirm: async () => {
        const rejectionReason = rejectionReasonValue.current.trim();
        if (!rejectionReason) {
          showError(translate('attendance:managedAttendance.modals.rejectDisabled.reasonRequired'));
          return;
        }
        if (!record.markId) {
          showError(translate('common:errors.unexpected'));
          closeModal('ConfirmModal');
          return;
        }
        setIsSubmitting(true);
        const response = await attendanceService.updateManagedMarkStatus(
          employeeId,
          record.markId,
          { status: 'rejected', comment: rejectionReason },
        );
        setIsSubmitting(false);
        if (response.success) {
          showNotification('success', translate('attendance:managedAttendance.messages.disabledRejected'), 4000);
          closeModal('ConfirmModal');
          refetchData();
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          showError(response.error.validationErrors[0].errorMessage ?? translate('common:errors.unexpected'));
          closeModal('ConfirmModal');
        } else {
          showApiError(response);
          closeModal('ConfirmModal');
        }
      },
      onCancel: () => closeModal('ConfirmModal'),
    });
  }, [
    openModal, closeModal, showNotification, showApiError,
    showError, translate, refetchData, employeeId,
  ]);

  const handleApproveModified = useCallback((record: ManagedAttendanceRecord) => {
    openModal('ConfirmModal', {
      variant: 'success',
      icon: 'check',
      title: translate('attendance:managedAttendance.modals.approveModified.title'),
      bodyText: translate('attendance:managedAttendance.modals.approveModified.body'),
      confirmText: translate('attendance:managedAttendance.modals.approveModified.confirm'),
      children: (
        <div className="flex flex-col gap-2 text-sm" style={{ color: colors.grays.neutral33 }}>
          <div className="flex justify-between">
            <span className="font-medium">{translate('attendance:managedAttendance.modals.approveModified.originalMarking')}</span>
            <span>{record.originalMarkTime || '-'}</span>
          </div>
          <div className="flex justify-between">
            <Text as="span" variant="subheader-medium">
              {translate('attendance:managedAttendance.modals.approveModified.modifiedMarking')}
            </Text>
            <Text as="span" variant="subheader-medium" color={colors.primary.main} style={{ fontWeight: 600 }}>
              {record.markingTime}
            </Text>
          </div>
        </div>
      ),
      onConfirm: async () => {
        if (!record.markId) {
          showError(translate('common:errors.unexpected'));
          closeModal('ConfirmModal');
          return;
        }
        setIsSubmitting(true);
        const response = await attendanceService.updateManagedMarkStatus(
          employeeId,
          record.markId,
          { status: 'approved', comment: '' },
        );
        setIsSubmitting(false);
        if (response.success) {
          showNotification('success', translate('attendance:managedAttendance.messages.modifiedApproved'), 4000);
          closeModal('ConfirmModal');
          refetchData();
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          showError(response.error.validationErrors[0].errorMessage ?? translate('common:errors.unexpected'));
          closeModal('ConfirmModal');
        } else {
          showApiError(response);
          closeModal('ConfirmModal');
        }
      },
      onCancel: () => closeModal('ConfirmModal'),
    });
  }, [
    openModal, closeModal, showNotification, showApiError,
    showError, translate, refetchData, employeeId,
  ]);

  const handleRejectModified = useCallback((record: ManagedAttendanceRecord) => {
    const rejectionReasonValue = { current: '' };
    openModal('ConfirmModal', {
      variant: 'error',
      icon: 'alert',
      title: translate('attendance:managedAttendance.modals.rejectModified.title'),
      bodyText: translate('attendance:managedAttendance.modals.rejectModified.body'),
      confirmText: translate('attendance:managedAttendance.modals.rejectModified.confirm'),
      children: (
        <>
          <div className="flex flex-col gap-2 text-sm" style={{ color: colors.grays.neutral33 }}>
            <div className="flex justify-between">
              <Text as="span" variant="subheader-medium">
                {translate('attendance:managedAttendance.modals.rejectModified.originalMarking')}
              </Text>
              <span>{record.originalMarkTime || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">
                {translate('attendance:managedAttendance.modals.rejectModified.modifiedMarking')}
              </span>
              <span style={{ color: colors.primary.main, fontWeight: 600 }}>
                {record.markingTime}
              </span>
            </div>
          </div>
          <RejectionReasonInput
            labelText={translate('attendance:managedAttendance.modals.rejectModified.reasonLabel')}
            placeholder={translate('attendance:managedAttendance.modals.rejectModified.reasonPlaceholder')}
            onValueChange={(value) => { rejectionReasonValue.current = value; }}
          />
        </>
      ),
      onConfirm: async () => {
        const rejectionReason = rejectionReasonValue.current.trim();
        if (!rejectionReason) {
          showError(translate('attendance:managedAttendance.modals.rejectModified.reasonRequired'));
          return;
        }
        if (!record.markId) {
          showError(translate('common:errors.unexpected'));
          closeModal('ConfirmModal');
          return;
        }
        setIsSubmitting(true);
        const response = await attendanceService.updateManagedMarkStatus(
          employeeId,
          record.markId,
          { status: 'rejected', comment: rejectionReason },
        );
        setIsSubmitting(false);
        if (response.success) {
          showNotification('success', translate('attendance:managedAttendance.messages.modifiedRejected'), 4000);
          closeModal('ConfirmModal');
          refetchData();
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          showError(response.error.validationErrors[0].errorMessage ?? translate('common:errors.unexpected'));
          closeModal('ConfirmModal');
        } else {
          showApiError(response);
          closeModal('ConfirmModal');
        }
      },
      onCancel: () => closeModal('ConfirmModal'),
    });
  }, [
    openModal, closeModal, showNotification, showApiError,
    showError, translate, refetchData, employeeId,
  ]);

  // Translation helpers for display values
  const { translateAction, translateStatus } = useMemo(
    () => createAttendanceTranslationHelpers(translate),
    [translate],
  );

  const columns: TableColumn<ManagedAttendanceRecord>[] = useMemo(
    () => [
      {
        key: 'creationDate',
        header: translate('attendance:managedAttendance.table.creationDate'),
        sortable: true,
      },
      {
        key: 'action',
        header: translate('attendance:managedAttendance.table.action'),
        sortable: true,
        render: (row) => translateAction(row.action),
      },
      {
        key: 'workdayDate',
        header: translate('attendance:managedAttendance.table.workdayDate'),
        sortable: true,
      },
      {
        key: 'markingTime',
        header: translate('attendance:managedAttendance.table.markingTime'),
        sortable: true,
      },
      {
        key: 'eventName',
        header: translate('attendance:managedAttendance.table.eventType'),
        sortable: true,
        render: (row) => row.eventName || '-',
      },
      {
        key: 'originalMarkTime',
        header: translate('attendance:managedAttendance.table.originalMarking'),
        sortable: false,
        render: (row) => {
          // Only show original mark for 'modified' actions
          if (row.action === 'modified') {
            return row.originalMarkTime || '-';
          }
          return '-';
        },
      },
      {
        key: 'modificationDate',
        header: translate('attendance:managedAttendance.table.modificationDate'),
        sortable: true,
        render: (row) => row.modificationDate ?? '-',
      },
      {
        key: 'status',
        header: translate('attendance:managedAttendance.table.status'),
        sortable: true,
        render: (row) => {
          const statusColorMap: Record<ManagedAttendanceStatus, string> = {
            approved: colors.feedback.success300,
            pending: colors.feedback.alert300,
            rejected: colors.feedback.error300,
          };
          return (
            <span style={{ color: statusColorMap[row.status], fontWeight: 500 }}>
              {translateStatus(row.status)}
            </span>
          );
        },
      },
    ],
    [translate, translateAction, translateStatus],
  );

  const actions: TableAction<ManagedAttendanceRecord>[] = useMemo(
    () => [
      {
        id: 'view',
        label: translate('attendance:managedAttendance.actions.viewDetails'),
        icon: 'eye',
        onClick: viewDetails,
        color: colors.important.main,
        // ASIS-4218: Always show view details
      },
      {
        id: 'approve',
        label: translate('attendance:managedAttendance.actions.approve'),
        icon: 'check',
        onClick: handleApprove,
        color: colors.feedback.success300,
        // ASIS-4217: Only show approve for 'created' action type with 'pending' status
        hidden: (row: ManagedAttendanceRecord) => row.action !== 'created' || row.status !== 'pending',
        disabled: () => isSubmitting,
      },
      {
        id: 'reject',
        label: translate('attendance:managedAttendance.actions.reject'),
        icon: 'close',
        onClick: handleReject,
        color: colors.feedback.error300,
        // ASIS-4217: Only show reject for 'created' action type with 'pending' status
        hidden: (row: ManagedAttendanceRecord) => row.action !== 'created' || row.status !== 'pending',
        disabled: () => isSubmitting,
      },
      {
        id: 'approve-disabled',
        label: translate('attendance:managedAttendance.actions.approve'),
        icon: 'check',
        onClick: handleApproveDisabled,
        color: colors.feedback.success300,
        // ASIS-4218: Only show approve for 'disabled' action type with 'pending' status
        hidden: (row: ManagedAttendanceRecord) => row.action !== 'disabled' || row.status !== 'pending',
        disabled: () => isSubmitting,
      },
      {
        id: 'reject-disabled',
        label: translate('attendance:managedAttendance.actions.reject'),
        icon: 'close',
        onClick: handleRejectDisabled,
        color: colors.feedback.error300,
        // ASIS-4218: Only show reject for 'disabled' action type with 'pending' status
        hidden: (row: ManagedAttendanceRecord) => row.action !== 'disabled' || row.status !== 'pending',
        disabled: () => isSubmitting,
      },
      {
        id: 'approve-modified',
        label: translate('attendance:managedAttendance.actions.approve'),
        icon: 'check',
        onClick: handleApproveModified,
        color: colors.feedback.success300,
        // ASIS-4219: Only show approve for 'modified' action type with 'pending' status
        hidden: (row: ManagedAttendanceRecord) => row.action !== 'modified' || row.status !== 'pending',
        disabled: () => isSubmitting,
      },
      {
        id: 'reject-modified',
        label: translate('attendance:managedAttendance.actions.reject'),
        icon: 'close',
        onClick: handleRejectModified,
        color: colors.feedback.error300,
        // ASIS-4219: Only show reject for 'modified' action type with 'pending' status
        hidden: (row: ManagedAttendanceRecord) => row.action !== 'modified' || row.status !== 'pending',
        disabled: () => isSubmitting,
      },
    ],
    [
      translate,
      viewDetails,
      handleApprove,
      handleReject,
      handleApproveDisabled,
      handleRejectDisabled,
      handleApproveModified,
      handleRejectModified,
      isSubmitting,
    ],
  );

  const setPageSizeCallback = useCallback((size: number) => {
    setPageSizeSafe(size);
  }, [setPageSizeSafe]);

  const setPageCallback = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const statusFilterOptions: StatusFilterOption[] = useMemo(
    () => [
      { value: 'all', label: translate('attendance:managedAttendance.filters.all') },
      { value: 'pending', label: translate('attendance:managedAttendance.status.pending') },
      { value: 'approved', label: translate('attendance:managedAttendance.status.approved') },
      { value: 'rejected', label: translate('attendance:managedAttendance.status.rejected') },
    ],
    [translate],
  );

  const actionFilterOptions: StatusFilterOption[] = useMemo(
    () => [
      { value: 'all', label: translate('attendance:managedAttendance.filters.all') },
      ...Object.entries(ATTENDANCE_ACTION_MAP).map(([value, translationKey]) => ({
        value,
        label: translate(translationKey),
      })),
    ],
    [translate],
  );

  return {
    filters,
    setFilters,
    defaultFrom,
    defaultTo,
    data: rows,
    totalRecords,
    isLoading,
    page,
    pageSize,
    setPage: setPageCallback,
    setPageSize: setPageSizeCallback,
    onSearch,
    onSortChange,
    columns,
    actions,
    viewDetails,
    statusFilterOptions,
    actionFilterOptions,
  };
};

export default useManagedAttendance;
